/**
 * The marker scheme (docs/UI.md "Markers and pointers"): one colour and one shape per MEANING, shared by the
 * minimap, the full map, its key, the screen pointer, the route arrows and the in-world beacons. Colour is
 * never the only cue — every family has its own shape too (colour-blind players read the shape). Pure.
 *
 *  goal    the running job's next stop   red diamond, pulsing; a red arrowhead on the rim; red route arrows
 *  target  part of the job (the motor…)  ink diamond with a red edge
 *  mark    the place YOU chose to go     plum flag; plum route arrows
 *  next    the suggested next thing      a dashed paper ring round the spot (no arrows unless you choose it)
 *  car     YOUR car (ownCar.ts)          a car silhouette in its own paint on a paper plate; on the rim with its distance
 *  friend  people with work              green disc, ink letter
 *  poi     places and shops              paper disc, ink letter
 *  event   (online) happenings, offers   mustard disc, ink letter; rings while live
 *  crew    (online) your crew            teal disc, ink initial; pinned to the rim
 *  gang    (online) your gang's members  ink disc with a teal edge, teal initial
 *  player  (online) anyone else          stone square, ink initial; orange square while wanted (2+ stars)
 *  hazard  danger: rivals, raids         orange triangle
 *  police  the police                    slate dot; flashing red / blue while they chase you
 */
import type { MapMarker, MarkerKind } from './uiTypes';

export const SCHEME = {
  goal: '#d03420',
  mark: '#c39ae6',
  work: '#8fb04a',
  place: '#e9e0c9',
  event: '#dba73f',
  crew: '#5fc2b0',
  danger: '#e8782a',
  police: '#7a8aa8',
  player: '#b9b09a',
} as const;

export type MarkerShape = 'diamond' | 'hollowDiamond' | 'flag' | 'ring' | 'disc' | 'hollowDisc' | 'square' | 'triangle' | 'dot' | 'car';

export interface MarkerStyle {
  /** Fill (the ring's stroke; the hollow diamond's edge). */
  colour: string;
  shape: MarkerShape;
  /** Pinned to the minimap's rim when out of range unless the marker says otherwise. */
  pin: boolean;
  /** Drawn above lower numbers where markers overlap; the rim keeps the highest when it must drop some. */
  priority: number;
}

export const MARKER_STYLE: Record<MarkerKind, MarkerStyle> = {
  objective: { colour: SCHEME.goal, shape: 'diamond', pin: true, priority: 100 },
  mark: { colour: SCHEME.mark, shape: 'flag', pin: true, priority: 90 },
  car: { colour: SCHEME.place, shape: 'car', pin: true, priority: 85 },
  target: { colour: SCHEME.goal, shape: 'hollowDiamond', pin: true, priority: 80 },
  hazard: { colour: SCHEME.danger, shape: 'triangle', pin: false, priority: 70 },
  next: { colour: SCHEME.place, shape: 'ring', pin: true, priority: 60 },
  crew: { colour: SCHEME.crew, shape: 'disc', pin: true, priority: 50 },
  gang: { colour: SCHEME.crew, shape: 'hollowDisc', pin: false, priority: 45 },
  player: { colour: SCHEME.player, shape: 'square', pin: false, priority: 25 },
  event: { colour: SCHEME.event, shape: 'disc', pin: false, priority: 40 },
  friend: { colour: SCHEME.work, shape: 'disc', pin: false, priority: 30 },
  police: { colour: SCHEME.police, shape: 'dot', pin: false, priority: 20 },
  poi: { colour: SCHEME.place, shape: 'disc', pin: false, priority: 10 },
};

/** Kinds that always keep their rim pin; the rest share RIM_EXTRA places, nearest first. */
const RIM_ALWAYS: ReadonlySet<MarkerKind> = new Set<MarkerKind>(['objective', 'mark', 'next', 'car']);
/** At most this many other markers pinned to the rim at once (crew, live events, the job's motors, raids). */
export const RIM_EXTRA = 4;

/** Does this marker ask to be pinned to the rim when it is out of range? */
export function wantsPin(m: MapMarker): boolean {
  return m.pin ?? MARKER_STYLE[m.kind].pin;
}

/**
 * Which out-of-range markers get a place on the rim (indices into `markers`, written to `out`): the goal, your
 * mark, your car and the suggestion always; of the rest that ask, the RIM_EXTRA nearest (a busy online city would
 * otherwise ring the minimap with arrows). `inRange` tells whether a marker is inside the circle.
 */
export function rimPins(markers: readonly MapMarker[], px: number, pz: number, inRange: (m: MapMarker) => boolean, out: Set<number>): Set<number> {
  out.clear();
  extraI.length = 0;
  for (let i = 0; i < markers.length; i++) {
    const m = markers[i]!;
    if (m.area || inRange(m) || !wantsPin(m)) continue;
    if (RIM_ALWAYS.has(m.kind)) out.add(i);
    else {
      extraD[i] = (m.x - px) ** 2 + (m.z - pz) ** 2;
      extraI.push(i);
    }
  }
  if (extraI.length > RIM_EXTRA) extraI.sort((a, b) => extraD[a]! - extraD[b]!);
  for (let k = 0; k < extraI.length && k < RIM_EXTRA; k++) out.add(extraI[k]!);
  return out;
}
// rimPins scratch (no allocation per frame once warm)
const extraI: number[] = [];
const extraD: number[] = [];

/** Marker indices in drawing order: lowest priority first, so the goal is never under a shop's disc. Stable. */
export function drawOrder(markers: readonly MapMarker[], out: number[]): number[] {
  out.length = 0;
  for (let i = 0; i < markers.length; i++) out.push(i);
  out.sort((a, b) => MARKER_STYLE[markers[a]!.kind].priority - MARKER_STYLE[markers[b]!.kind].priority || a - b);
  return out;
}
